import { LuChevronDown } from '@react-icons/all-files/lu/LuChevronDown'
import { LuChevronRight } from '@react-icons/all-files/lu/LuChevronRight'
import { SEARCH_PAGE_LOCATION } from '@disco/state-management/selectors/media'
import React, { useMemo } from 'react'

import type { Track } from 'types/track'

import MediaOverlay from 'components/common/media-overlay'
import TrackArtwork from 'components/common/tracks/track-artwork'
import { Flex, IconButton, Text } from 'components/ds'
import type { SearchColumnProps } from 'components/search/search-columns/types'
import { HoverProvider } from 'components/ui/hover-provider'

import styles from './track-title-column.module.css'

export const TrackTitleColumn = React.memo(({ info }: SearchColumnProps) => {
  const name = info.getValue<string>()
  const track = info.row.original as Track

  // Get tracklist from table meta (passed from SearchPage via DataTable)
  const tracklist = (info.table.options.meta as { tracklist?: number[] })
    ?.tracklist

  const playbackContext = useMemo(
    () => ({
      type: track.type,
      playbackLocation: SEARCH_PAGE_LOCATION,
      tracklist,
    }),
    [track.type, tracklist],
  )

  // Dynamic width computed from CSS variable for column sizing
  const dynamicWidth = useMemo(
    () =>
      `calc(var(--col-${String(info.column.id)
        .toLowerCase()
        .replace(/\s+/g, '-')}-size) * 1px - 32px)`,
    [info.column.id],
  )

  const canExpand = info.row.getCanExpand()
  const isExpanded = info.row.getIsExpanded()

  return (
    <Flex align="center" gap="2" pl={info.row.depth > 0 ? '5' : undefined}>
      {canExpand && (
        <IconButton
          variant="ghost"
          size="1"
          aria-label={isExpanded ? 'Collapse track' : 'Expand track'}
          onClick={e => {
            // Keep the expand toggle from also triggering row selection.
            e.stopPropagation()
            info.row.toggleExpanded()
          }}
        >
          {isExpanded ? <LuChevronDown /> : <LuChevronRight />}
        </IconButton>
      )}
      <HoverProvider>
        <Flex width="24px" height="24px">
          <MediaOverlay
            track={track}
            mediaSource="in-app"
            playbackContext={playbackContext}
            iconSize={12}
          >
            <TrackArtwork track={track} />
          </MediaOverlay>
        </Flex>
      </HoverProvider>
      <Flex className={styles.titleText} width={dynamicWidth} translate="no">
        <Text>{name}</Text>
      </Flex>
    </Flex>
  )
})

TrackTitleColumn.displayName = 'TrackTitleColumn'
